import { createStore } from "@stencil/store";
import {
  baseCartApiUrl,
  baseSiteId,
  userId,
} from "frontend-kit/organisms/f-header/f-cart-flyout/f-cart-flyout-config";
import {
  ContextService,
  JwtService,
  RequestHeaderService,
} from "globals/helpers";
import Cart from "api/model/mini-cart/cart";
import GetCartsResponse from "@apis/responses/get-user-carts-response";
import { FlyoutState, Price } from "api/model/core";
import { AddToCartRequest } from "@apis/requests/add-to-cart-request";
import {
  UpdatedEntry,
  UpdatedEntryResponse,
} from "@apis/responses/add-to-cart-response";
import config from "../environment/environment-config";

interface CartStore {
  carts: Cart[];
  loading: boolean;
  initialized: boolean;
  is: FlyoutState;
}

const { state, set } = createStore({
  carts: [],
  loading: false,
  initialized: false,
} as CartStore);

function handleErrors() {
  set("is", "ERROR");
}

function stopLoading() {
  set("loading", false);
}

/**
 * Sends a request to the API to create a new cart.
 */
export function createCart(): Promise<Cart> {
  const url = `${baseCartApiUrl}/${baseSiteId}/users/${userId}/carts`;
  return fetch(url, {
    method: "POST",
    ...RequestHeaderService.getHeaders(),
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error();
      }
      const cart: Cart = await response.json();
      set("carts", [cart, ...state.carts]);
      return cart;
    })
    .finally(stopLoading);
}

function fetchCartData(
  url: string,
  isGettingSingleCart = false
): Promise<void> {
  return fetch(url, {
    method: "GET",
    ...RequestHeaderService.getHeadersWithJwt(),
  }).then(async (response) => {
    if (!response.ok) {
      throw new Error();
    }
    if (!isGettingSingleCart) {
      const { carts }: GetCartsResponse = await response.json();
      set("carts", carts);
    } else {
      const cart: Cart = await response.json();
      set("carts", [cart]);
    }
  });
}

export function getCarts(): void {
  if (ContextService.isAnonymous()) {
    const anonymousCartGuid = config.cartConfig.cartGuid;
    fetchCartForAnonymous(anonymousCartGuid);
  } else {
    fetchCartsForContextUser();
  }
  set("initialized", true);
}

/**
 * Sends request to get a cart (with the given ID - cartId) for an anonymous user.
 */
function fetchCartForAnonymous(cartGuid: string): void {
  const url = `${baseCartApiUrl}/${baseSiteId}/users/${userId}/carts/${cartGuid}`;
  fetchCartData(url, true).catch(handleErrors).finally(stopLoading);
}

/**
 * Sends request to get a cart (with the given ID - cartId) for a context user.
 */
function fetchCartsForContextUser(): void {
  JwtService.validateJwt()
    .then(() => {
      const url = `${baseCartApiUrl}/${baseSiteId}/users/${userId}/carts?fields=FULL`;
      fetchCartData(url).catch(handleErrors).finally(stopLoading);
    })
    .catch(handleErrors);
}

function addEntriesToCart(
  updatedEntries: UpdatedEntry[],
  totalPrice: Price,
  cart: Cart
): Cart {
  let { entries = [], totalItems } = cart;
  updatedEntries.forEach((updatedEntry) => {
    if (updatedEntry.newLine) {
      entries = [updatedEntry.entry, ...entries];
      totalItems++;
    } else {
      entries = entries.map((entry) => {
        if (entry.entryNumber === updatedEntry.entry.entryNumber) {
          entry.quantity += updatedEntry.quantityAdded;
          entry.totalPrice = updatedEntry.entry.totalPrice;
        }
        return entry;
      });
    }
  });

  return { ...cart, entries, totalPrice, totalItems };
}

function handleAddToCart(
  { totalPrice, updatedEntries }: UpdatedEntryResponse,
  cartCode: string
): Cart[] {
  return state.carts.map((cart) => {
    if (
      ContextService.isAnonymous()
        ? cart.guid === cartCode
        : cart.code === cartCode
    ) {
      return addEntriesToCart(updatedEntries, totalPrice, cart);
    }
    return cart;
  });
}

function resetErrorMessages(severities: string[]): void {
  severities.forEach((severity) => {
    const containerElement = document.getElementById(
      `CartComponentHeader${severity}MessageContainer`
    );
    const messageElement = document.getElementById(
      `CartComponentHeader${severity}Message`
    );

    containerElement?.classList.add("hidden");

    if (messageElement) {
      messageElement.innerHTML = "";
    }
  });
}

function setErrorMessage(severity: string, message: string): void {
  const containerElement = document.getElementById(
    `CartComponentHeader${severity}MessageContainer`
  );
  const messageElement = document.getElementById(
    `CartComponentHeader${severity}Message`
  );

  containerElement?.classList.remove("hidden");

  if (messageElement) {
    messageElement.innerHTML = message;
  }
}

/**
 * Sends request to add new entries to the cart
 *
 * @param request - request object (AddToCartRequest)
 * @see AddToCartRequest
 */
export async function addToCart(
  request: AddToCartRequest
): Promise<UpdatedEntryResponse | void> {
  if (!ContextService.isAnonymous()) {
    await JwtService.validateJwt();
  }

  const cartCode = ContextService.isAnonymous()
    ? request.cart.guid
    : request.cart.code;
  const url = `${baseCartApiUrl}/${baseSiteId}/users/${userId}/carts/${cartCode}/entries/add?fields=FULL`;

  return fetch(url, {
    method: "POST",
    body: JSON.stringify(request.order),
    ...RequestHeaderService.getHeadersWithJwt(),
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error();
      }
      const updatedEntryResponse: UpdatedEntryResponse = await response.json();

      const severities = ["Info", "Warning", "Error"];
      resetErrorMessages(severities);
      const entry = updatedEntryResponse.updatedEntries[0];

      severities.forEach((severity) => {
        const message = entry[`${severity.toLowerCase()}Notifications`];
        if (message) {
          setErrorMessage(severity, message[0].text);
          return;
        }
      });

      if (entry.errorNotifications) {
        return;
      }

      const updatedCarts = handleAddToCart(updatedEntryResponse, cartCode);

      set("carts", updatedCarts);
      return updatedEntryResponse;
    })
    .catch(handleErrors)
    .finally(stopLoading);
}

/**
 * Removes the product with the given number (entryNumber) from the specific cart (cartCode).
 *
 * @param cartCode - Cart code from which we want to remove the product
 * @param entryNumber - Number of the article we want to delete
 */
export function deleteEntry(cartCode: string, entryNumber: number): void {
  set("loading", true);

  JwtService.validateJwt()
    .then(() => {
      const url = `${baseCartApiUrl}/${baseSiteId}/users/${userId}/carts/${cartCode}/entries/${entryNumber}`;

      return fetch(url, {
        method: "DELETE",
        ...RequestHeaderService.getHeadersWithJwt(),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error();
          }
          set("carts", [
            ...state.carts.map((cart) => {
              if (cart.code === cartCode) {
                return {
                  ...cart,
                  entries: cart.entries.filter(
                    (entry) => entry.entryNumber !== entryNumber
                  ),
                };
              }
              return cart;
            }),
          ]);
        })
        .catch(handleErrors);
    })
    .catch(handleErrors)
    .finally(stopLoading);
}

/**
 * Sends a request to the API to merge two baskets into one.
 *
 * @param newCartGuid - Cart ID to merge into
 */
export const mergeCarts = (newCartGuid?: string): Promise<void> => {
  const oldCartGuid = config.cartConfig.cartGuid;
  let url = `${baseCartApiUrl}/${baseSiteId}/users/${userId}/carts?oldCartId=${oldCartGuid}`;
  url += newCartGuid != null ? `&toMergeCartGuid=${newCartGuid}` : "";

  return fetch(url, {
    method: "POST",
    ...RequestHeaderService.getHeadersWithJwt(),
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error();
      }
      const updatedCart: Cart = await response.json();

      state.carts.forEach((cart) =>
        cart.guid === updatedCart.guid ? updatedCart : cart
      );
    })
    .catch(handleErrors)
    .finally(stopLoading);
};

export default state;
